import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Descendant } from 'slate'
import { convex } from '../lib/convex'
import { api } from '../convex_generated/api'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../contexts/ToastContext'
import RichTextEditor, { EMPTY_SLATE_CONTENT, countWords } from '../components/editor/RichTextEditor'
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
  PlusIcon,
  CloudArrowUpIcon,
  ScissorsIcon,
  RocketLaunchIcon,
  LockOpenIcon,
  LockClosedIcon,
  BookOpenIcon,
  CodeBracketIcon,
  XMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import { BOOK_GENRES, type BookGenre } from '../constants/genres'
import { asId } from '../lib/ids'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Categories a branch can be created with from the editor. */
type CreatableCategory = 'backstory' | 'parallel-worlds' | 'expanded-reality' | 'general' | 'origin-story'
/** Every category an existing branch can have (linked branches are created from the book page). */
type BranchCategory = CreatableCategory | 'linked-branch'

const CATEGORY_COLORS: Record<BranchCategory, string> = {
  general:            'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  backstory:          'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  'parallel-worlds':  'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  'expanded-reality': 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  'origin-story':     'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  'linked-branch':    'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
}

const CATEGORY_DESCRIPTIONS: Record<CreatableCategory, string> = {
  general:            'Continue or reimagine the story freely',
  backstory:          'Explore what happened before this moment',
  'parallel-worlds':  'Take the narrative into a different reality',
  'expanded-reality': 'Zoom into a side character, place, or event',
  'origin-story':     'Tell the backstory of a specific character',
}
type Visibility = 'public' | 'private' | 'paid' | 'in-progress'

interface Book {
  _id: string
  title: string
  originalAuthor: string
  coverImage: string
}

interface NewBranchForm {
  title: string
  bookId: string
  category: CreatableCategory
  visibility: Visibility
  price: number
  characterName: string
}

// ---------------------------------------------------------------------------
// New Story Modal (original work — creates book + root branch)
// ---------------------------------------------------------------------------

const GENRES = BOOK_GENRES
type GenreLiteral = BookGenre

const NewStoryModal: React.FC<{ onClose: () => void; onCreated: (branchId: string) => void }> = ({ onClose, onCreated }) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [selectedGenres, setSelectedGenres] = useState<GenreLiteral[]>(['fiction'])
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggleGenre = (g: GenreLiteral) => {
    setSelectedGenres((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : prev.length < 3 ? [...prev, g] : prev
    )
  }

  const handleCreate = async () => {
    if (!title.trim()) { setError('Title is required'); return }
    setError(null)
    setIsCreating(true)
    try {
      const result = await convex.mutation(api.books.createOriginalStory, {
        title: title.trim(),
        description: description.trim(),
        genre: selectedGenres,
      }) as { bookId: string; branchId: string }
      onCreated(result.branchId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create story')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <BookOpenIcon className="w-5 h-5 text-primary-500" />
            New Original Story
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Create a brand new story that you own. Others will be able to branch off your work.
        </p>

        {error && (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-sm">
            <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div>
          <label className="label-text">Story Title</label>
          <input
            autoFocus
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder="e.g. The Last Cartographer"
            className="input w-full mt-1"
          />
        </div>

        <div>
          <label className="label-text">Short description <span className="text-gray-400">(optional)</span></label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="A few sentences about your story…"
            className="input w-full mt-1 resize-none"
          />
        </div>

        <div>
          <label className="label-text mb-1.5 block">Genre <span className="text-gray-400 text-xs">(pick up to 3)</span></label>
          <div className="flex flex-wrap gap-2">
            {GENRES.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => toggleGenre(g)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize border transition-colors ${
                  selectedGenres.includes(g)
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="btn btn-ghost flex-1">Cancel</button>
          <button onClick={handleCreate} disabled={isCreating} className="btn btn-primary flex-1">
            {isCreating ? 'Creating…' : 'Create & Start Writing'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// New Branch Modal (branches off an existing book/branch, requires anchor point)
// ---------------------------------------------------------------------------

interface NewBranchModalProps {
  books: Book[]
  defaultBookId?: string
  defaultParentId?: string
  currentBranchTitle?: string
  onClose: () => void
  onCreated: (branchId: string) => void
  userId: string
}

const NewBranchModal: React.FC<NewBranchModalProps> = ({ books, defaultBookId, defaultParentId, currentBranchTitle, onClose, onCreated }) => {
  const [form, setForm] = useState<NewBranchForm>(() => ({
    title: '',
    bookId: defaultBookId ?? '',
    category: 'general',
    visibility: 'private',
    price: 0,
    characterName: '',
  }))
  const [forkContext, setForkContext] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Once books load, pick the first if no default is set
  useEffect(() => {
    if (!form.bookId && books.length > 0) {
      setForm((f) => ({ ...f, bookId: defaultBookId ?? books[0]._id }))
    }
  }, [books, defaultBookId, form.bookId])

  const handleCreate = async () => {
    if (!form.title.trim()) { setError('Title is required'); return }
    if (!form.bookId) { setError('Select a book'); return }
    if (!forkContext.trim()) { setError('Paste the excerpt from the story where your branch diverges'); return }
    if (form.category === 'origin-story' && !form.characterName.trim()) { setError('Character name is required for Origin Story'); return }
    setError(null)
    setIsCreating(true)

    try {
      const branchId = await convex.mutation(api.branches.createBranch, {
        bookId: asId<"books">(form.bookId),
        parentBranchId: asId<"branches">(defaultParentId),
        title: form.title.trim(),
        category: form.category,
        visibility: form.visibility,
        price: form.visibility === 'paid' ? form.price : undefined,
        forkContext: forkContext.trim(),
        characterName: form.category === 'origin-story' ? form.characterName.trim() : undefined,
      })
      onCreated(branchId as string)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create branch')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <CodeBracketIcon className="w-5 h-5 text-primary-500" />
            New Branch
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-sm">
            <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Anchor excerpt — required */}
        <div>
          <label className="label-text">Branching point <span className="text-red-500">*</span></label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-1.5">
            Paste a sentence from {currentBranchTitle ? `"${currentBranchTitle}"` : 'the story'} where your branch diverges. This anchors your branch in the text.
          </p>
          <textarea
            value={forkContext}
            onChange={(e) => setForkContext(e.target.value)}
            rows={3}
            placeholder={`e.g. "She turned left at the crossroads, never knowing what lay to the right."`}
            className="input w-full resize-none text-sm"
          />
        </div>

        <div>
          <label className="label-text">Branch Title <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder="e.g. The Path Not Taken"
            className="input w-full mt-1"
            autoFocus
          />
        </div>

        <div>
          <label className="label-text">Book</label>
          {books.length === 0 ? (
            <p className="text-sm text-gray-500 mt-1">No books available.</p>
          ) : (
            <select
              value={form.bookId}
              onChange={(e) => setForm((f) => ({ ...f, bookId: e.target.value }))}
              className="input w-full mt-1"
            >
              {books.map((b) => (
                <option key={b._id} value={b._id}>{b.title}</option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="label-text">Category</label>
          <div className="grid grid-cols-2 gap-1.5 mt-1">
            {(Object.keys(CATEGORY_DESCRIPTIONS) as CreatableCategory[]).map((key) => (
              <div key={key} className="relative group">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, category: key }))}
                  className={`w-full py-1.5 px-2.5 rounded-lg text-xs font-medium border-2 transition-colors capitalize ${
                    form.category === key ? 'border-primary-500' : 'border-transparent'
                  } ${CATEGORY_COLORS[key]}`}
                >
                  {key.replace(/-/g, ' ')}
                </button>
                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-20">
                  <div className="bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-snug rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg">
                    {CATEGORY_DESCRIPTIONS[key]}
                  </div>
                  <div className="w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45 mx-auto -mt-1" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {form.category === 'origin-story' && (
          <div>
            <label className="label-text">Character name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.characterName}
              onChange={(e) => setForm((f) => ({ ...f, characterName: e.target.value }))}
              placeholder="e.g. Elizabeth Bennet"
              className="input w-full mt-1"
            />
            <p className="text-xs text-gray-400 mt-1">
              Their name will be highlighted throughout the book, linking readers to this origin story.
            </p>
          </div>
        )}

        <div>
          <label className="label-text">Visibility</label>
          <select value={form.visibility} onChange={(e) => setForm((f) => ({ ...f, visibility: e.target.value as Visibility }))} className="input w-full mt-1">
            <option value="private">Private (draft)</option>
            <option value="public">Public (free)</option>
            <option value="paid">Paid</option>
          </select>
        </div>

        {form.visibility === 'paid' && (
          <div>
            <label className="label-text">Price (StoryCoins)</label>
            <input type="number" min={1} value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: Number(e.target.value) }))} className="input w-full mt-1" />
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="btn btn-ghost flex-1">Cancel</button>
          <button onClick={handleCreate} disabled={isCreating || books.length === 0} className="btn btn-primary flex-1">
            {isCreating ? 'Creating…' : 'Create Branch'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Save status indicator
// ---------------------------------------------------------------------------

const SaveIndicator: React.FC<{ status: 'saved' | 'saving' | 'error'; lastSaved?: number }> = ({ status, lastSaved }) => {
  const icon = {
    saved: <CheckCircleIcon className="w-4 h-4 text-green-500" />,
    saving: <CloudArrowUpIcon className="w-4 h-4 text-blue-500 animate-pulse" />,
    error: <ExclamationCircleIcon className="w-4 h-4 text-red-500" />,
  }[status]

  const label = {
    saved: lastSaved ? `Saved ${new Date(lastSaved).toLocaleTimeString()}` : 'Saved',
    saving: 'Saving…',
    error: 'Save failed',
  }[status]

  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
      {icon}
      {label}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Publish modal
// ---------------------------------------------------------------------------

const STORYCOINS_PER_GBP = 100

interface PublishModalProps {
  currentVisibility: string
  currentPrice?: number
  onClose: () => void
  onPublish: (visibility: 'public' | 'paid' | 'in-progress', price?: number) => Promise<void>
  onUnpublish: () => Promise<void>
}

const PublishModal: React.FC<PublishModalProps> = ({ currentVisibility, currentPrice, onClose, onPublish, onUnpublish }) => {
  const isDraft = currentVisibility === 'private'
  const [visibility, setVisibility] = useState<'public' | 'paid' | 'in-progress'>(
    currentVisibility === 'paid' ? 'paid' : currentVisibility === 'in-progress' ? 'in-progress' : 'public'
  )
  const [price, setPrice] = useState<number>(currentPrice ?? 100)
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handlePublish = async () => {
    if (visibility === 'paid' && !(Number.isInteger(price) && price >= 1)) {
      setError('Price must be a whole number of at least 1 StoryCoin')
      return
    }
    setError(null)
    setIsBusy(true)
    try { await onPublish(visibility, visibility === 'paid' ? price : undefined) }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to publish') }
    finally { setIsBusy(false) }
  }

  const handleUnpublish = async () => {
    setIsBusy(true)
    try { await onUnpublish() }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to retract') }
    finally { setIsBusy(false) }
  }

  const gbpPreview = visibility === 'paid' ? (price / STORYCOINS_PER_GBP).toFixed(2) : null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <RocketLaunchIcon className="w-5 h-5 text-primary-500" />
            {isDraft ? 'Publish branch' : 'Update pricing'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isDraft
              ? 'Your draft will become visible to others.'
              : 'Change the visibility or price of your published branch.'}
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-sm">
            <ExclamationCircleIcon className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {/* Visibility selector */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {(['public', 'paid'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setVisibility(v)}
                className={`p-3 rounded-xl border-2 text-left transition-all ${
                  visibility === v
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {v === 'public'
                    ? <LockOpenIcon className="w-4 h-4 text-green-500" />
                    : <LockClosedIcon className="w-4 h-4 text-amber-500" />}
                  <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 capitalize">
                    {v === 'public' ? 'Free' : 'Paid'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {v === 'public' ? 'Anyone can read for free.' : 'Readers pay in StoryCoins.'}
                </p>
              </button>
            ))}
          </div>

          {/* In Progress — opt-in */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200 dark:border-gray-700" /></div>
            <div className="relative flex justify-center"><span className="bg-white dark:bg-gray-800 px-2 text-xs text-gray-400">or publish while writing</span></div>
          </div>
          <button
            onClick={() => setVisibility('in-progress')}
            className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
              visibility === 'in-progress'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <ClockIcon className="w-4 h-4 text-blue-500" />
              <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">In Progress</span>
              <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">optional</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Share a readable draft while you write. Readers can leave paragraph comments.
            </p>
          </button>
        </div>

        {/* Price input */}
        {visibility === 'paid' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Price (StoryCoins)
            </label>
            <input
              type="number"
              min={1}
              max={100000}
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              className="input w-full"
            />
            {gbpPreview && (
              <p className="text-xs text-gray-400 mt-1">
                ≈ £{gbpPreview} &nbsp;·&nbsp; You earn £{(Number(gbpPreview) * 0.75).toFixed(2)} after 25% platform fee
              </p>
            )}
          </div>
        )}

        {/* Earnings note */}
        <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg p-3 text-xs">
          StoryBranch takes a <strong>25% commission</strong>. Your earnings are credited in StoryCoins and can be
          withdrawn to GBP (minimum £{10}).
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="btn btn-ghost flex-1">Cancel</button>
          {!isDraft && (
            <button onClick={handleUnpublish} disabled={isBusy} className="btn btn-ghost flex-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
              Make draft
            </button>
          )}
          <button onClick={handlePublish} disabled={isBusy} className="btn btn-primary flex-1">
            {isBusy ? 'Publishing…' : isDraft ? 'Publish' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main EditorPage
// ---------------------------------------------------------------------------

const EditorPage: React.FC = () => {
  const { branchId } = useParams<{ branchId?: string }>()
  const [searchParams] = useSearchParams()
  const defaultBookId = searchParams.get('bookId') ?? undefined
  const defaultParentId = searchParams.get('parentId') ?? undefined

  const navigate = useNavigate()
  const { user } = useAuth()
  const { showToast } = useToast()

  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved')
  const [lastSaved, setLastSaved] = useState<number | undefined>(undefined)

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // With nothing to edit, start by creating something: a branch when the link
  // says what to branch from ("Fork this branch"), otherwise a new story.
  const isForkLink = !branchId && (!!defaultBookId || !!defaultParentId)
  const [showNewBranchModal, setShowNewBranchModal] = useState(isForkLink)
  const [showNewStoryModal, setShowNewStoryModal] = useState(!branchId && !isForkLink)
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [books, setBooks] = useState<Book[]>([])
  const [branch, setBranchData] = useState<{
    _id: string; title: string; category: BranchCategory; visibility: Visibility;
    wordCount: number; content: Descendant[]; forkContext?: string; price?: number; publishedAt?: number
  } | null>(null)
  const [localContent, setLocalContent] = useState<Descendant[]>(EMPTY_SLATE_CONTENT)
  const [wordCount, setWordCount] = useState(0)

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastContentRef = useRef<Descendant[] | null>(null)

  // Load books for the new-branch modal
  useEffect(() => {
    convex.query(api.books.listBooks, {}).then((result) => {
      setBooks(result.map((b) => ({ _id: b._id, title: b.title, originalAuthor: b.originalAuthor, coverImage: b.coverImage })))
    }).catch(() => setBooks([]))
  }, [])

  // Load the branch when branchId is in the URL
  useEffect(() => {
    if (!branchId) { setIsLoading(false); return }

    setIsLoading(true)
    convex.query(api.branches.getBranch, { branchId: asId<"branches">(branchId) })
      .then(async (result) => {
        if (!result) { setError('Branch not found'); return }

        let content: Descendant[] = EMPTY_SLATE_CONTENT
        if (result.contentUrl) {
          try {
            const nodes = await fetch(result.contentUrl).then((r) => r.json())
            if (Array.isArray(nodes) && nodes.length > 0) content = nodes as Descendant[]
          } catch {
            // fall back to empty content
          }
        } else if (Array.isArray(result.content) && result.content.length > 0) {
          // legacy inline content
          content = result.content as Descendant[]
        }

        setBranchData({ _id: result._id, title: result.title, category: result.category, visibility: result.visibility, wordCount: result.wordCount ?? 0, content, forkContext: result.forkContext, price: result.price, publishedAt: result.publishedAt })
        lastContentRef.current = content
        setLocalContent(content)
        setWordCount(result.wordCount ?? 0)
      })
      .catch(() => setError('Failed to load branch'))
      .finally(() => setIsLoading(false))
  }, [branchId])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    }
  }, [])

  /** Upload Slate content to Convex File Storage and update the branch record. */
  const saveContentToStorage = useCallback(async (content: Descendant[], wc: number) => {
    if (!branchId) throw new Error('No branch')
    // 1. Get upload URL
    const uploadUrl = await convex.mutation(api.branches.generateBranchUploadUrl)
    // 2. Upload content as JSON blob
    const blob = new Blob([JSON.stringify(content)], { type: 'application/json' })
    const res = await fetch(uploadUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: blob })
    if (!res.ok) throw new Error('Upload failed')
    const { storageId } = await res.json()
    // 3. Update branch with new storageId
    await convex.mutation(api.branches.updateBranchContentFromStorage, {
      branchId: asId<"branches">(branchId),
      storageId,
      content, // passed for forkPosition recalculation
      wordCount: wc,
    })
  }, [branchId])

  // Auto-save: 2s debounce after content change
  const triggerAutoSave = useCallback(
    (content: Descendant[]) => {
      if (!branchId || !user) return
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)

      saveTimeoutRef.current = setTimeout(async () => {
        setSaveStatus('saving')
        try {
          const wc = countWords(content)
          await saveContentToStorage(content, wc)
          setSaveStatus('saved')
          setLastSaved(Date.now())
        } catch {
          setSaveStatus('error')
        }
      }, 2000)
    },
    [branchId, user, saveContentToStorage],
  )

  const handleContentChange = useCallback(
    (value: Descendant[]) => {
      // Slate also fires onChange when only the selection moves; the document
      // is then the same array. Nothing to save — and saving would needlessly
      // re-upload the whole branch on every click.
      if (value === lastContentRef.current) return
      lastContentRef.current = value
      setLocalContent(value)
      setWordCount(countWords(value))
      triggerAutoSave(value)
    },
    [triggerAutoSave],
  )

  const handleManualSave = async () => {
    if (!branchId || !user) return
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    setSaveStatus('saving')
    try {
      await saveContentToStorage(localContent, wordCount)
      setSaveStatus('saved')
      setLastSaved(Date.now())
      showToast({ message: 'Branch saved', type: 'success' })
    } catch {
      setSaveStatus('error')
      showToast({ message: 'Save failed', type: 'error' })
    }
  }

  const handleBranchCreated = (newBranchId: string) => {
    setShowNewBranchModal(false)
    setShowNewStoryModal(false)
    navigate(`/editor/${newBranchId}`, { replace: true })
  }

  const handlePublish = async (visibility: 'public' | 'paid' | 'in-progress', price?: number) => {
    if (!branchId) return
    await convex.mutation(api.payments.publishBranch, { branchId: asId<"branches">(branchId), visibility, price })
    setBranchData((prev) => prev ? { ...prev, visibility, price, publishedAt: prev.publishedAt ?? Date.now() } : prev)
    setShowPublishModal(false)
    showToast({ message: visibility === 'paid' ? `Published at ${price} StoryCoins` : 'Published for free!', type: 'success' })
  }

  const handleUnpublish = async () => {
    if (!branchId) return
    await convex.mutation(api.payments.unpublishBranch, { branchId: asId<"branches">(branchId) })
    setBranchData((prev) => prev ? { ...prev, visibility: 'private', price: undefined } : prev)
    setShowPublishModal(false)
    showToast({ message: 'Branch moved back to drafts', type: 'success' })
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (isLoading && branchId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <ExclamationCircleIcon className="w-12 h-12 text-red-500 mx-auto" />
          <p className="text-gray-700 dark:text-gray-300">{error}</p>
          <button onClick={() => navigate(-1)} className="btn btn-primary">Go Back</button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* New Story modal */}
      {showNewStoryModal && (
        <NewStoryModal
          onClose={() => (branchId ? setShowNewStoryModal(false) : navigate('/'))}
          onCreated={handleBranchCreated}
        />
      )}

      {/* New Branch modal */}
      {showNewBranchModal && (
        <NewBranchModal
          books={books}
          defaultBookId={defaultBookId}
          defaultParentId={defaultParentId}
          currentBranchTitle={branch?.title}
          onClose={() => setShowNewBranchModal(false)}
          onCreated={handleBranchCreated}
          userId={user?._id ?? ''}
        />
      )}

      {/* Publish modal */}
      {showPublishModal && branch && (
        <PublishModal
          currentVisibility={branch.visibility}
          currentPrice={branch.price}
          onClose={() => setShowPublishModal(false)}
          onPublish={handlePublish}
          onUnpublish={handleUnpublish}
        />
      )}

      {/* Top bar */}
      <div className="sticky top-16 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 flex-shrink-0">
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                {branch?.title ?? 'New Branch'}
              </h1>
              {branch && (
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {branch.category.replace('-', ' ')} ·{' '}
                  {branch.visibility === 'private' ? '📝 Draft' : branch.visibility === 'in-progress' ? '🔄 In Progress' : branch.visibility === 'paid' ? `🔒 ${branch.price} StoryCoins` : '🌐 Public'}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:block">
              {wordCount.toLocaleString()} words
            </span>
            {branchId && <SaveIndicator status={saveStatus} lastSaved={lastSaved} />}
            <button onClick={() => setShowNewBranchModal(true)} className="btn btn-ghost btn-sm flex items-center gap-1">
              <PlusIcon className="w-4 h-4" /> Branch
            </button>            {branchId && (
              <button onClick={handleManualSave} className="btn btn-ghost btn-sm">
                Save
              </button>
            )}
            {branchId && (
              <button
                onClick={() => setShowPublishModal(true)}
                className={`btn btn-sm flex items-center gap-1 ${
                  branch?.visibility === 'private'
                    ? 'btn-primary'
                    : branch?.visibility === 'in-progress'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {branch?.visibility === 'in-progress'
                  ? <><ClockIcon className="w-4 h-4" /> In Progress</>
                  : <><RocketLaunchIcon className="w-4 h-4" />{branch?.visibility === 'private' ? 'Publish' : 'Published'}</>}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Editor */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {branch?.forkContext && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-4 flex gap-3">
            <ScissorsIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">Branched from</p>
              <p className="text-sm text-amber-800 dark:text-amber-200 italic">&ldquo;{branch.forkContext}&rdquo;</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Write your alternative story starting from this point.</p>
            </div>
          </div>
        )}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {branchId ? (
            <RichTextEditor
              content={localContent}
              onChange={handleContentChange}
              placeholder="Begin your story…"
            />
          ) : (
            <div className="p-16 text-center text-gray-500 dark:text-gray-400 space-y-6">
              <p className="text-lg">What would you like to create?</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <button
                  onClick={() => setShowNewStoryModal(true)}
                  className="flex-1 flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all group"
                >
                  <BookOpenIcon className="w-8 h-8 text-primary-500 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Original Story</p>
                    <p className="text-xs text-gray-400 mt-1">Your own universe, your rules</p>
                  </div>
                </button>
                <button
                  onClick={() => setShowNewBranchModal(true)}
                  className="flex-1 flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all group"
                >
                  <CodeBracketIcon className="w-8 h-8 text-primary-500 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Branch</p>
                    <p className="text-xs text-gray-400 mt-1">Diverge from an existing story</p>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EditorPage
